/**
 * Delicious Music Player – Multi-Instance Edition
 * ・複数プレイヤー対応（グローバル変数排除）
 * ・全DOMクエリをコンテナスコープ内に限定
 * ・IDからクラスへ変更（重複排除）
 * ・alert()をUI表示に変更
 * ・touchstart passive対応
 * ・画像比較ロジック改善
 */
(function () {
  'use strict';

  /**
   * 1つのプレイヤーコンテナを初期化する
   * @param {HTMLElement} container
   */
  function initPlayer(container) {

    /* --- プレイリストデータをJSONスクリプトタグから読み込む --- */
    const jsonEl = container.querySelector('.dm-playlist-json');
    if (!jsonEl) return;

    let playlists;
    try {
      playlists = JSON.parse(jsonEl.textContent);
    } catch (e) {
      console.error('DM: Failed to parse playlist data', e);
      return;
    }

    /* --- DOMエレメント（全てcontainerスコープ） --- */
    const controls     = container.querySelector('.dm-playlist-controls');
    const nowPlayingEl = container.querySelector('.dm-now-playing');
    const stopBtn      = container.querySelector('.dm-stop-button');
    const seekBar      = container.querySelector('.dm-seek-bar');
    const helpTextEl   = container.querySelector('.dm-help-text');
    const visualArea   = container.querySelector('.dm-visual');
    const visualImage  = container.querySelector('.dm-visual-image');
    const visualVideo  = container.querySelector('.dm-visual-video');

    /* --- 状態管理（このプレイヤー専用） --- */
    const audio = new Audio();
    audio.loop = true;

    let currentTrackUrl    = null;
    let isDragging         = false;
    let slideshowInterval  = null;
    let currentImageIndex  = 0;
    let currentTrackImages = [];

    initPlaylist();

    /* --- Shopifyテーマエディタ イベント対応 --- */
    document.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail.blockId;
      // 自分のプレイリストのブロックのみ反応
      const selectedTrack = playlists.find(t => t.id === blockId);
      if (selectedTrack) {
        updateUI(selectedTrack, false);
        if (!audio.paused) stopTrack();
      }
    });

    /* =========================================
       プレイリスト初期化
       ========================================= */
    function initPlaylist() {
      if (!controls) return;
      controls.innerHTML = '';

      if (playlists.length === 0) {
        if (nowPlayingEl) nowPlayingEl.textContent = 'No tracks available. Add tracks in Theme Editor.';
        return;
      }

      playlists.forEach((track) => {
        const btn = document.createElement('button');
        btn.className = 'dm-track-btn';
        btn.type = 'button';
        btn.dataset.blockId = track.id;

        btn.innerHTML = `
          <div class="dm-track-icon">${track.icon}</div>
          <span class="dm-track-title">${track.title}</span>
        `;

        if (track.status === 'coming') {
          btn.classList.add('is-coming');
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.innerHTML += `<span class="dm-status-label">Coming Soon</span>`;
        } else {
          btn.addEventListener('click', () => {
            if (!track.fileUrl) {
              // alert()の代わりにUIで表示
              if (nowPlayingEl) nowPlayingEl.textContent = 'Error: Audio file not found.';
              return;
            }
            // 同じ曲が再生中ならトグルでストップ
            if (currentTrackUrl === track.fileUrl && !audio.paused) {
              stopTrack();
              return;
            }
            playTrack(track);
          });
        }

        controls.appendChild(btn);
      });

      if (stopBtn) stopBtn.addEventListener('click', stopTrack);
      setupSeekBar();
      setupAudioListeners();
    }

    /* =========================================
       再生・停止・UI更新
       ========================================= */
    function playTrack(track) {
      if (currentTrackUrl === track.fileUrl && !audio.paused) return;

      currentTrackUrl = track.fileUrl;
      audio.src = track.fileUrl;
      audio.load();

      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => console.error('DM: Playback error:', error));
      }

      updateUI(track, true);
    }

    function stopTrack() {
      audio.pause();
      audio.currentTime = 0;
      currentTrackUrl = null;

      stopSlideshow();
      resetVisual();

      if (nowPlayingEl) nowPlayingEl.textContent = 'Select a track';
      if (seekBar) {
        seekBar.value = 0;
        seekBar.disabled = true;
        seekBar.style.backgroundSize = '0% 100%';
      }

      // このコンテナ内のボタンのみリセット
      container.querySelectorAll('.dm-track-btn').forEach(b => b.classList.remove('active'));
    }

    function updateUI(track, autoPlay = true) {
      if (nowPlayingEl) nowPlayingEl.textContent = `Now Playing: ${track.title}`;
      if (helpTextEl) helpTextEl.style.display = 'none';

      // このコンテナ内のボタンのみ操作
      container.querySelectorAll('.dm-track-btn').forEach(b => b.classList.remove('active'));

      // data-block-idで確実に特定（テキスト比較から変更）
      const activeBtn = container.querySelector(`.dm-track-btn[data-block-id="${track.id}"]`);
      if (activeBtn) activeBtn.classList.add('active');

      stopSlideshow();

      // 動画がある場合
      if (track.videoUrl && visualVideo) {
        if (visualArea) visualArea.classList.add('is-video-active');
        visualVideo.src = track.videoUrl;
        if (autoPlay) {
          visualVideo.play().catch(e => console.log('DM: Video play error:', e));
        } else {
          visualVideo.pause();
          visualVideo.currentTime = 0;
        }
        return;
      }

      resetVisual();

      currentTrackImages = track.images || [];
      currentImageIndex = 0;

      if (currentTrackImages.length > 0 && visualImage) {
        changeImage(currentTrackImages[0]);
        if (currentTrackImages.length > 1 && autoPlay) {
          startSlideshow();
        }
      }
    }

    /* =========================================
       ビジュアル（画像・動画）制御
       ========================================= */
    function resetVisual() {
      if (visualArea) visualArea.classList.remove('is-video-active');
      if (visualImage) visualImage.classList.remove('dm-zoom');
      if (visualVideo) {
        visualVideo.pause();
        setTimeout(() => {
          if (visualArea && !visualArea.classList.contains('is-video-active')) {
            visualVideo.src = '';
          }
        }, 1200);
      }
    }

    function startSlideshow() {
      slideshowInterval = setInterval(() => {
        currentImageIndex = (currentImageIndex + 1) % currentTrackImages.length;
        changeImage(currentTrackImages[currentImageIndex]);
      }, 6000);
    }

    function stopSlideshow() {
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
        slideshowInterval = null;
      }
    }

    function changeImage(newUrl) {
      if (!visualImage) return;

      // URLを正規化して比較（http/httpsの差異を吸収）
      const currentSrc = visualImage.getAttribute('src') || '';
      if (currentSrc.replace(/^https?:/, '') === newUrl.replace(/^https?:/, '')) return;

      visualImage.style.opacity = '0';

      setTimeout(() => {
        if (visualImage.hasAttribute('srcset')) visualImage.removeAttribute('srcset');
        visualImage.classList.remove('dm-zoom');
        visualImage.src = newUrl;

        const onImageReady = () => {
          if (visualArea && !visualArea.classList.contains('is-video-active')) {
            void visualImage.offsetWidth; // アニメーション再起動のためのreflow
            visualImage.classList.add('dm-zoom');
            visualImage.style.opacity = '1';
          }
        };

        // naturalWidthで実際に読み込まれているか確認
        if (visualImage.complete && visualImage.naturalWidth > 0) {
          onImageReady();
        } else {
          visualImage.onload = onImageReady;
        }
      }, 1200);
    }

    /* =========================================
       オーディオ・シークバー
       ========================================= */
    function setupAudioListeners() {
      audio.addEventListener('timeupdate', () => {
        if (seekBar && !isDragging && audio.duration) {
          const progress = (audio.currentTime / audio.duration) * 100;
          seekBar.value = progress;
          seekBar.disabled = false;
          seekBar.style.backgroundSize = `${progress}% 100%`;
        }
      });

      if (!audio.loop) {
        audio.addEventListener('ended', stopTrack);
      }
    }

    function setupSeekBar() {
      if (!seekBar) return;

      seekBar.addEventListener('mousedown', () => { isDragging = true; });
      // passiveオプションでモバイルスクロールのパフォーマンス改善
      seekBar.addEventListener('touchstart', () => { isDragging = true; }, { passive: true });

      seekBar.addEventListener('change', () => {
        if (audio.duration) {
          audio.currentTime = (seekBar.value / 100) * audio.duration;
        }
        isDragging = false;
      });

      seekBar.addEventListener('input', () => {
        isDragging = true;
        seekBar.style.backgroundSize = `${seekBar.value}% 100%`;
      });
    }
  }

  /* =========================================
     ページ内の全プレイヤーを初期化
     ========================================= */
  function initAllPlayers() {
    document.querySelectorAll('.dm-container').forEach(initPlayer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPlayers);
  } else {
    // DOMContentLoadedが既に発火済みの場合（deferなしで読み込まれた場合）
    initAllPlayers();
  }

})();
